import React, { useState } from "react";
import axios from "axios";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["D2C", "Logistics", "SaaS", "Tech", "Manufacturing", "Fintech", "Agritech", "Healthtech", "Other"];
const STAGES = ["Idea", "Pre-revenue", "Pre-seed", "Seed", "Pre-Series A", "Series A"];

export default function PitchForm({ open, onClose }) {
  const [form, setForm] = useState({
    founder_name: "", startup_name: "", sector: "D2C", city: "",
    stage: "Idea", traction: "", deck_url: "", email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/pitch`, form);
      setSuccess(true);
      toast.success("Pitch submitted. We'll reach out within 7 days.");
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setForm({ founder_name: "", startup_name: "", sector: "D2C", city: "", stage: "Idea", traction: "", deck_url: "", email: "" });
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed. Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="pitch-form-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-[#0F2818]/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-2xl bg-[#F7F8EF] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div className="px-7 py-5 border-b border-[#14401F]/10 flex items-center justify-between bg-[#14401F] text-white">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] font-semibold">
              For Founders
            </div>
            <h3 className="font-serif-display text-2xl font-bold mt-1">Pitch to Us</h3>
          </div>
          <button
            data-testid="pitch-form-close"
            onClick={onClose}
            className="h-10 w-10 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="flex-1 flex flex-col items-center justify-center px-7 py-16 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#14401F] mb-4" />
            <h4 className="font-serif-display text-2xl font-bold text-[#0F2818]">
              Pitch received.
            </h4>
            <p className="mt-2 text-[#4A5A4F]">
              We'll review and respond within 7 days.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto px-7 py-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Founder Name" testid="pitch-founder-name" value={form.founder_name} onChange={(v) => set("founder_name", v)} required />
              <Field label="Startup Name" testid="pitch-startup-name" value={form.startup_name} onChange={(v) => set("startup_name", v)} required />
              <Select label="Sector" testid="pitch-sector" value={form.sector} onChange={(v) => set("sector", v)} options={SECTORS} />
              <Field label="City" testid="pitch-city" value={form.city} onChange={(v) => set("city", v)} required placeholder="e.g. Coimbatore" />
              <Select label="Stage" testid="pitch-stage" value={form.stage} onChange={(v) => set("stage", v)} options={STAGES} />
              <Field label="Email" testid="pitch-email" value={form.email} onChange={(v) => set("email", v)} type="email" required />
            </div>
            <Field label="Traction (revenue, users, pilots)" testid="pitch-traction" value={form.traction} onChange={(v) => set("traction", v)} placeholder="e.g. ₹15L MRR, 200 paying customers" />
            <Field label="Deck URL" testid="pitch-deck-url" value={form.deck_url} onChange={(v) => set("deck_url", v)} placeholder="Notion, Google Drive, DocSend link" />

            <button
              type="submit"
              data-testid="pitch-submit"
              disabled={submitting}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#14401F] text-[#B8D958] hover:bg-[#0F2818] disabled:opacity-60 font-semibold rounded-full px-7 py-4 transition-all"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Submitting…" : "Submit Pitch"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[#14401F] font-semibold mb-2">
        {label} {required && <span className="text-[#E8A33D]">*</span>}
      </span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-[#14401F]/15 bg-white px-4 py-3 text-sm text-[#0F2818] placeholder:text-[#4A5A4F]/50 focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/40 transition-all"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[#14401F] font-semibold mb-2">
        {label}
      </span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-[#14401F]/15 bg-white px-4 py-3 text-sm text-[#0F2818] focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/40 transition-all"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
